import { parseSseFrame } from "../util/streamUtils";

const DEFAULT_MAX_SSE_FRAME_BYTES = 1024 * 1024;
const DEFAULT_MAX_RECONNECTS = 5;
const DEFAULT_RECONNECT_DELAY_MS = 1000;
const CONNECTION_LOST_MESSAGE = "Connection lost. Check your internet connection and try again.";
const INVALID_STREAM_MESSAGE = "Primepoint returned an invalid conversation stream.";
const OVERSIZED_FRAME_MESSAGE = "Primepoint returned an oversized conversation stream frame.";

export class AuthenticatedSseTerminalError extends Error {
    /** The HTTP status that refused the stream, when a response did. */
    readonly status: number | undefined;
    constructor(message: string, status?: number) {
        super(message);
        this.name = "AuthenticatedSseTerminalError";
        this.status = status;
    }
}

export class AuthenticatedSseSilentClose extends Error {
    constructor() {
        super("Authenticated SSE closed without a client error");
        this.name = "AuthenticatedSseSilentClose";
    }
}

export interface AuthenticatedSseOpenRequest {
    lastEventId: string | null;
    signal: AbortSignal;
}

export interface AuthenticatedSseOpenResult {
    response: Response;
}

export interface AuthenticatedSseOptions<T> {
    open(request: AuthenticatedSseOpenRequest): Promise<Response | AuthenticatedSseOpenResult>;
    decode(data: string): T;
    isTerminal(event: T): boolean;
    onEvent(event: T): void;
    /** `status`: the HTTP status that refused the stream, when a response did. */
    onTerminalError(message: string, reconnectsExhausted: boolean, status?: number): void;
    onClosed?(): void;
    initialEventId?: string | null;
    delay?(milliseconds: number, signal: AbortSignal): Promise<void>;
    maxReconnects?: number;
    reconnectDelayMs?: number;
    maxFrameBytes?: number;
    retryStatusCodes?: readonly number[];
    resetReconnectsAfterMs?: number;
    /** Reconnect from the last event when an open response delivers no byte for this
     * long, so a socket that died without closing cannot hold the stream forever. Only
     * for servers that keep a quiet stream alive with comments well within it. */
    idleTimeoutMs?: number;
}

/** The last event id applied, advanced as frames arrive so a connection that fails
 * mid-response (a read error, the idle timeout) reconnects from where it got to. */
interface SseCursor {
    lastEventId: string | null;
}

class SseProtocolError extends Error {}

class SseIdleTimeout extends Error {}

function utf8Size(value: string): number {
    return new TextEncoder().encode(value).byteLength;
}

/** Applies the response's frames; true once a terminal event arrived. */
async function consumeResponse<T>(
    response: Response,
    cursor: SseCursor,
    options: AuthenticatedSseOptions<T>,
    maxFrameBytes: number,
): Promise<boolean> {
    const contentType = response.headers.get("Content-Type")?.toLowerCase() ?? "";
    if (!response.body || !contentType.startsWith("text/event-stream")) {
        throw new AuthenticatedSseTerminalError(INVALID_STREAM_MESSAGE);
    }
    const reader = response.body.getReader();
    const idleTimeoutMs = options.idleTimeoutMs;
    const read = (): Promise<ReadableStreamReadResult<Uint8Array>> => {
        if (!idleTimeoutMs) return reader.read();
        let timer: ReturnType<typeof setTimeout> | undefined;
        const idle = new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject(new SseIdleTimeout()), idleTimeoutMs);
        });
        return Promise.race([reader.read(), idle]).finally(() => clearTimeout(timer));
    };
    const decoder = new TextDecoder();
    let buffer = "";

    const consumeFrame = (rawFrame: string): boolean => {
        if (utf8Size(rawFrame) > maxFrameBytes) throw new SseProtocolError(OVERSIZED_FRAME_MESSAGE);
        const frame = parseSseFrame(rawFrame);
        if (!frame.data.trim()) return false;
        if (frame.id && frame.id === cursor.lastEventId) return false;
        let event: T;
        try {
            event = options.decode(frame.data);
        } catch {
            throw new SseProtocolError(INVALID_STREAM_MESSAGE);
        }
        if (frame.id) cursor.lastEventId = frame.id;
        options.onEvent(event);
        return options.isTerminal(event);
    };

    try {
        while (true) {
            const chunk = await read();
            if (chunk.done) break;
            buffer += decoder.decode(chunk.value, { stream: true }).replace(/\r/g, "");
            let boundary = buffer.indexOf("\n\n");
            while (boundary !== -1) {
                if (consumeFrame(buffer.slice(0, boundary))) return true;
                buffer = buffer.slice(boundary + 2);
                boundary = buffer.indexOf("\n\n");
            }
            if (utf8Size(buffer) > maxFrameBytes) {
                throw new SseProtocolError(OVERSIZED_FRAME_MESSAGE);
            }
        }
        buffer += decoder.decode().replace(/\r/g, "");
        return Boolean(buffer.trim()) && consumeFrame(buffer);
    } catch (error) {
        await reader.cancel(error).catch(() => undefined);
        throw error;
    } finally {
        reader.releaseLock();
    }
}

function defaultDelay(milliseconds: number, signal: AbortSignal): Promise<void> {
    if (milliseconds <= 0 || signal.aborted) return Promise.resolve();
    return new Promise((resolve) => {
        const timer = setTimeout(resolve, milliseconds);
        signal.addEventListener(
            "abort",
            () => {
                clearTimeout(timer);
                resolve();
            },
            { once: true },
        );
    });
}

export function createAuthenticatedSseStream<T>(options: AuthenticatedSseOptions<T>): () => void {
    const controller = new AbortController();
    const delay = options.delay ?? defaultDelay;
    const maxReconnects = options.maxReconnects ?? DEFAULT_MAX_RECONNECTS;
    const reconnectDelayMs = options.reconnectDelayMs ?? DEFAULT_RECONNECT_DELAY_MS;
    const maxFrameBytes = options.maxFrameBytes ?? DEFAULT_MAX_SSE_FRAME_BYTES;
    let closed = false;
    const cursor: SseCursor = { lastEventId: options.initialEventId ?? null };

    const close = (): void => {
        if (closed) return;
        closed = true;
        controller.abort();
        options.onClosed?.();
    };
    const finishError = (message: string, reconnectsExhausted = false, status?: number): void => {
        if (closed) return;
        close();
        options.onTerminalError(message, reconnectsExhausted, status);
    };

    void (async () => {
        let reconnects = 0;
        while (!closed) {
            let connectedAt: number | undefined;
            try {
                const opened = await options.open({
                    lastEventId: cursor.lastEventId,
                    signal: controller.signal,
                });
                if (closed) return;
                const response =
                    typeof opened === "object" && opened !== null && "response" in opened
                        ? opened.response
                        : opened;
                if (!response.ok) {
                    if (options.retryStatusCodes?.includes(response.status)) {
                        await response.body?.cancel().catch(() => undefined);
                        throw new Error(CONNECTION_LOST_MESSAGE);
                    }
                    throw new AuthenticatedSseTerminalError(
                        response.status === 401
                            ? "Cannot validate session"
                            : CONNECTION_LOST_MESSAGE,
                        response.status,
                    );
                }
                connectedAt = Date.now();
                if (await consumeResponse(response, cursor, options, maxFrameBytes)) {
                    close();
                    return;
                }
            } catch (error) {
                if (closed || controller.signal.aborted) return;
                if (error instanceof AuthenticatedSseSilentClose) {
                    close();
                    return;
                }
                if (error instanceof AuthenticatedSseTerminalError) {
                    finishError(error.message, false, error.status);
                    return;
                }
                if (error instanceof SseProtocolError) {
                    finishError(error.message);
                    return;
                }
            }
            if (
                connectedAt !== undefined &&
                options.resetReconnectsAfterMs &&
                Date.now() - connectedAt >= options.resetReconnectsAfterMs
            )
                reconnects = 0;
            reconnects += 1;
            if (reconnects >= maxReconnects) {
                finishError(CONNECTION_LOST_MESSAGE, true);
                return;
            }
            await delay(reconnects * reconnectDelayMs, controller.signal);
        }
    })();

    return close;
}
